/**
 * Founder-side floating notes button + panel showing notes left by their EIR.
 *
 * Polls every 30s for new notes and badges the unseen count (notes newer than
 * the server-tracked last_seen_at). Opening the panel marks notes seen. Notes
 * are grouped by page; clicking a note routes to that page when one is mapped.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyEIRNotes, markNotesAsSeen } from '../services/apiService';
import { chipFor, initials } from '../utils/authorStyle';

const PAGE_LABELS = {
  data_room:   'Data Room',
  fundraising: 'Fundraising Plan',
  crm:         'Investor Hygiene',
};

const DEFAULT_TZ = 'America/New_York';

function formatNoteDate(iso) {
  if (!iso) return '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TZ;
  return new Date(iso).toLocaleString(undefined, {
    timeZone: tz,
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

const PAGE_ROUTES = {
  data_room:   '/data-room',
  fundraising: '/fundraising',
  crm:         '/crm',
};

export default function FounderNotesWidget() {
  const navigate = useNavigate();

  const [isOpen, setIsOpen]       = useState(false);
  const [notes, setNotes]         = useState([]);
  const [lastSeenAt, setLastSeenAt] = useState(null);

  useEffect(() => {
    function fetchNotes() {
      getMyEIRNotes()
        .then(({ notes: n, last_seen_at }) => {
          setNotes(n || []);
          setLastSeenAt((prev) => prev ?? last_seen_at ?? null);
        })
        .catch(() => {});
    }

    fetchNotes();
    const interval = setInterval(fetchNotes, 30_000);
    return () => clearInterval(interval);
  }, []);

  // A note is "unseen" if it postdates the last time the panel was opened
  // (or if we have no last_seen_at yet).
  const unseenCount = notes.filter(
    (n) => !lastSeenAt || new Date(n.created_at) > new Date(lastSeenAt)
  ).length;

  const pages = [...new Set(notes.map((n) => n.page || 'general'))];

  function openPanel() {
    setIsOpen(true);
    markNotesAsSeen()
      .then(({ last_seen_at }) => setLastSeenAt(last_seen_at))
      .catch(() => {});
  }

  function handleNoteClick(page) {
    const route = PAGE_ROUTES[page];
    if (route) {
      setIsOpen(false);
      navigate(route);
    }
  }

  return (
    <>
      {isOpen && (
        <div style={s.panel} className="notes-panel">
          <div style={s.panelHeader}>
            <span style={s.panelTitle}>Notes from your EIR</span>
            <button style={s.closeBtn} onClick={() => setIsOpen(false)}>✕</button>
          </div>
          <div style={s.panelBody}>
            {notes.length === 0 ? (
              <p style={s.empty}>Your EIR hasn't left any notes yet.</p>
            ) : (
              pages.map((page) => (
                <div key={page} style={s.pageGroup}>
                  <p style={s.pageLabel}>
                    {PAGE_LABELS[page] || page}
                    {PAGE_ROUTES[page] && (
                      <span style={s.pageLink} onClick={() => handleNoteClick(page)}>
                        Go to {PAGE_LABELS[page]} →
                      </span>
                    )}
                  </p>
                  {notes
                    .filter((n) => (n.page || 'general') === page)
                    .map((n) => {
                      // Founder view: notes come from EIRs (never the founder), so
                      // all use the neutral tone; the initials avatar names each author.
                      const c = chipFor(false);
                      const [fn, ...rest] = (n.eir_name || '').split(' ');
                      return (
                        <div
                          key={n.id}
                          style={{ ...s.noteCard, borderLeft: `3px solid ${c.border}`, ...(PAGE_ROUTES[page] ? s.noteCardClickable : {}) }}
                          onClick={() => PAGE_ROUTES[page] && handleNoteClick(page)}
                          title={PAGE_ROUTES[page] ? `Go to ${PAGE_LABELS[page]}` : undefined}
                        >
                          <div style={s.noteHeader}>
                            <span style={{ ...s.avatar, background: c.bg, color: c.fg }}>
                              {initials(fn, rest.join(' '))}
                            </span>
                            <span style={s.noteAuthor}>{n.eir_name}</span>
                            <span style={s.noteMeta}>{formatNoteDate(n.created_at)}</span>
                          </div>
                          <p style={s.noteBody}>{n.body}</p>
                        </div>
                      );
                    })}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Floating button — badge only shows while panel is closed */}
      <button style={s.bubble} className="notes-bubble" onClick={() => isOpen ? setIsOpen(false) : openPanel()} title="Notes from your EIR">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {unseenCount > 0 && <span style={s.badge}>{unseenCount}</span>}
      </button>
    </>
  );
}

const s = {
  bubble: {
    position: 'fixed', bottom: 28, right: 28, zIndex: 300,
    width: 48, height: 48, borderRadius: '50%',
    background: 'var(--color-forest)',
    border: 'none', cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(33,81,45,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  badge: {
    position: 'absolute', top: -4, right: -4,
    background: '#c66b47', color: '#fff',
    borderRadius: '50%', width: 18, height: 18,
    fontSize: 10, fontWeight: 700, fontFamily: 'var(--font-body)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    pointerEvents: 'none',
  },
  panel: {
    position: 'fixed', bottom: 88, right: 28, zIndex: 300,
    width: 600, maxHeight: 740,
    background: '#fff', borderRadius: 'var(--radius-lg)',
    boxShadow: '0 8px 40px rgba(33,81,45,0.18)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  panelHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', borderBottom: '1px solid var(--color-mint-dark)',
    background: 'var(--color-frost)',
  },
  panelTitle: { fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 700, color: 'var(--color-forest)' },
  closeBtn:   { background: 'none', border: 'none', fontSize: 18, color: 'var(--color-forest)', opacity: 0.5, cursor: 'pointer', lineHeight: 1 },
  panelBody:  { overflowY: 'auto', padding: '18px 22px', flex: 1 },
  empty:      { fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-forest)', opacity: 0.5, textAlign: 'center', padding: '28px 0' },
  pageGroup:  { marginBottom: 22 },
  pageLabel:  {
    fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700,
    color: 'var(--color-forest)', opacity: 0.5,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  pageLink: {
    fontSize: 13, fontWeight: 700, color: 'var(--color-forest)',
    opacity: 1, cursor: 'pointer', textDecoration: 'underline',
    textTransform: 'none', letterSpacing: 0,
  },
  noteCard: {
    background: 'var(--color-frost)', border: '1px solid var(--color-mint-dark)',
    borderRadius: 'var(--radius-sm)', padding: '14px 18px', marginBottom: 10,
  },
  noteCardClickable: {
    cursor: 'pointer',
  },
  noteHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  avatar:     { width: 24, height: 24, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 800 },
  noteAuthor: { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: 'var(--color-forest)' },
  noteBody:   { fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-forest)', lineHeight: 1.65 },
  noteMeta:   { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.45, marginLeft: 'auto' },
};
